import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LaunchSignup } from "@/components/LaunchSignup";
import { BetaFeedbackButton } from "@/components/BetaFeedback";
import { useBetaStatus, betaMetric } from "@/hooks/useBetaStatus";
import { BellRing, CheckCircle2, Lock, Sparkles } from "lucide-react";

const CAN_DO = [
  "You can explore WitnessYourVote",
  "Learn how Mutual Witness works",
  "Explore Community Voice",
  "View candidate information as it becomes available",
  "Sign up for updates",
  "Report problems or suggestions",
];

export default function BetaInfo() {
  const beta = useBetaStatus();
  const msg = beta?.messaging || {};

  useEffect(() => { betaMetric("beta_explainer_visit"); }, []);

  useEffect(() => {
    if (window.location.hash === "#signup") {
      setTimeout(() => document.getElementById("signup")?.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, []);

  return (
    <div className="wyv-container py-10 sm:py-14 max-w-3xl" data-testid="beta-info-page">
      <Badge variant="outline" className="bg-primary text-white border-primary mb-4 text-[10px] font-bold tracking-widest">
        <Sparkles className="h-3 w-3 mr-1" /> BETA
      </Badge>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="beta-info-title">
        What does BETA mean?
      </h1>
      <p className="text-muted-foreground mt-3 leading-relaxed text-lg">
        WitnessYourVote is being opened early so people can see it and help us test it
        before the full public election program begins.
      </p>

      <Card className="mt-8">
        <CardContent className="p-6">
          <h2 className="font-heading font-bold text-base text-foreground mb-4">During BETA:</h2>
          <ul className="space-y-2.5" data-testid="beta-can-do-list">
            {CAN_DO.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-foreground">
                <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> {item}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card className="mt-4 border-slate-300 bg-secondary/60">
        <CardContent className="p-6">
          <p className="font-heading font-semibold text-sm text-foreground inline-flex items-center gap-2" data-testid="beta-not-live-note">
            <Lock className="h-4 w-4 text-slate-500" /> But:
          </p>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            The full WitnessYourVote election-verification program has <strong className="text-foreground">not officially launched yet</strong>.
            When it does, we'll notify everyone who signed up.
          </p>
          <div className="mt-4">
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="beta-notify-cta">
              <a href="#signup"><BellRing className="h-4 w-4 mr-1.5" /> NOTIFY ME AT LAUNCH</a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* BETA vs OFFICIAL — always visually distinct */}
      <div className="grid sm:grid-cols-2 gap-4 mt-8" data-testid="beta-vs-official">
        <Card className="border-t-4 border-t-primary">
          <CardContent className="p-5">
            <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 text-[10px] mb-2">BETA — now</Badge>
            <p className="text-sm text-foreground leading-relaxed">
              Explore, test, learn, provide feedback and sign up for updates.
              WitnessYourVote is now <strong>open in BETA</strong>.
            </p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-slate-400">
          <CardContent className="p-5">
            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[10px] mb-2">OFFICIAL PUBLIC IMPLEMENTATION — coming</Badge>
            <p className="text-sm text-foreground leading-relaxed">
              Actual operational WitnessYourVote election participation. Not yet live —
              it begins only when the platform officially launches.
            </p>
          </CardContent>
        </Card>
      </div>

      <div id="signup" className="mt-10 scroll-mt-24">
        <LaunchSignup variant="full" sourcePage="/beta" />
      </div>

      <Card className="mt-6">
        <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-sm text-foreground">HELP US IMPROVE WITNESSYOURVOTE</p>
            <p className="text-xs text-muted-foreground mt-1">
              Spotted something confusing or broken during the BETA? Tell us — it goes straight to the team.
            </p>
          </div>
          <BetaFeedbackButton />
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground mt-8 leading-relaxed">
        WitnessYourVote is here — but we're still in BETA. Explore it now, help us improve it, and
        sign up so you'll be among the first to know when the full system officially launches.{" "}
        <Link to="/how-it-works" className="text-primary hover:underline">Learn how WitnessYourVote works →</Link>
      </p>
    </div>
  );
}
